import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, HelpCircle, TrendingDown } from "lucide-react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { DailyActivityChart } from "@/components/analytics/daily-activity-chart";
import { CrmCompanyCard } from "@/components/crm/crm-company-card";
import { PlatformStageBadge } from "@/components/crm/status-badges";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAccessProfile } from "@/hooks/use-admin-auth";
import { formatMrr, formatTimeAgo } from "@/lib/sales-format";
import { client } from "@/utils/orpc";

export const Route = createFileRoute("/sales/$organizationId")({
	component: SalesOrgDetailPage,
});

function SalesOrgDetailPage() {
	const { organizationId } = Route.useParams();
	const { t, i18n } = useTranslation();
	const { profile } = useAccessProfile();

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "sales", "detail", organizationId],
		queryFn: async () => client.admin.sales.getOrganization({ organizationId }),
		staleTime: 60 * 1000,
	});

	if (isLoading || !data) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-10 w-1/3" />
				<Skeleton className="h-40" />
				<Skeleton className="h-64" />
			</div>
		);
	}

	const dateFormat = new Intl.DateTimeFormat(i18n.language, {
		dateStyle: "medium",
	});
	const formatDate = (value: string | Date | null): string =>
		value ? dateFormat.format(new Date(value)) : "—";

	return (
		<div className="space-y-6">
			<div className="flex items-start justify-between">
				<div>
					<h1 className="flex items-center gap-3 font-bold text-3xl">
						{data.organization.name}
						<PlatformStageBadge stage={data.stage} />
						{data.churnRisk && (
							<Badge variant="destructive" title={t("admin:sales.churn_risk")}>
								<TrendingDown className="mr-1 h-3 w-3" />
								{t("admin:sales.churn_risk")}
							</Badge>
						)}
					</h1>
					{data.organization.retentionEndsAt && (
						<p className="mt-1 text-destructive text-sm">
							{t("admin:sales.detail.retention_notice", {
								date: formatDate(data.organization.retentionEndsAt),
							})}
						</p>
					)}
				</div>
				{/* Support org-detail stays support-gated — only link when the
				    viewer actually holds support access. */}
				{profile?.canProvideSupport && (
					<Button variant="outline" asChild>
						<Link
							to="/organizations/$organizationId"
							params={{ organizationId }}
						>
							{t("admin:sales.detail.open_in_support")}
						</Link>
					</Button>
				)}
			</div>

			<CrmCompanyCard
				platformOrganizationId={organizationId}
				platformOrganizationName={data.organization.name}
			/>

			<div className="grid gap-4 md:grid-cols-2">
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							{t("admin:sales.detail.mrr_total")}
							{data.mrr.mixedCurrency && (
								<AlertTriangle
									className="h-4 w-4 text-amber-500"
									aria-label={t("admin:sales.mixed_currency")}
								/>
							)}
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						<p className="font-bold text-3xl">
							{formatMrr(
								data.mrr.totalCents,
								data.mrr.currencyCode,
								i18n.language,
							)}
						</p>
						{data.mrr.perSub.length > 0 && (
							<div className="space-y-1">
								<p className="font-medium text-muted-foreground text-sm">
									{t("admin:sales.detail.mrr_breakdown")}
								</p>
								{data.mrr.perSub.map((sub) => {
									const enriched = data.subscriptions.find(
										(s) => s.id === sub.subscriptionId,
									);
									return (
										<div
											key={sub.subscriptionId}
											className="flex justify-between text-sm"
										>
											<span>
												{enriched?.planName ??
													enriched?.projectName ??
													sub.subscriptionId}
											</span>
											<span>
												{formatMrr(sub.cents, sub.currencyCode, i18n.language)}
											</span>
										</div>
									);
								})}
							</div>
						)}
						{data.mrr.skippedItems > 0 && (
							<p className="text-amber-600 text-sm">
								{t("admin:sales.detail.skipped_items", {
									count: data.mrr.skippedItems,
								})}
							</p>
						)}
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>{t("admin:sales.detail.activity")}</CardTitle>
					</CardHeader>
					<CardContent>
						<dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
							{(
								[
									{
										key: "last_activity",
										value: data.activity.lastActivityAt
											? `${formatDate(data.activity.lastActivityAt)} (${formatTimeAgo(
													new Date(data.activity.lastActivityAt),
													i18n.language,
												)})`
											: "—",
									},
									{
										key: "active_members_30d",
										value: data.activity.activeMembers30d,
									},
									{
										key: "actions_30d",
										value:
											Object.entries(data.activity.countsByAction)
												.map(([action, value]) => `${action}: ${value}`)
												.join(", ") || "—",
									},
									{ key: "members", value: data.activity.memberCount },
									{ key: "projects", value: data.activity.projectCount },
									{ key: "files", value: data.activity.fileCount },
									{ key: "drawings", value: data.activity.drawingCount },
								] as const
							).map(({ key, value }) => (
								<Fragment key={key}>
									<dt className="flex items-center gap-1 text-muted-foreground">
										{t(`admin:sales.detail.${key}`)}
										<Tooltip>
											<TooltipTrigger asChild>
												<HelpCircle
													className="h-3.5 w-3.5 cursor-help"
													aria-label={t(`admin:sales.detail.tooltip.${key}`)}
												/>
											</TooltipTrigger>
											<TooltipContent className="max-w-xs">
												{t(`admin:sales.detail.tooltip.${key}`)}
											</TooltipContent>
										</Tooltip>
									</dt>
									<dd>{value}</dd>
								</Fragment>
							))}
						</dl>
					</CardContent>
				</Card>
			</div>

			<DailyActivityChart
				title={t("admin:sales.detail.daily_activity")}
				description={t("admin:sales.detail.daily_activity_description")}
				data={data.activity.dailyActivity}
			/>

			<Card>
				<CardHeader>
					<CardTitle>{t("admin:sales.detail.subscriptions")}</CardTitle>
				</CardHeader>
				<CardContent>
					{data.subscriptions.length === 0 ? (
						<p className="text-muted-foreground text-sm">
							{t("admin:sales.detail.no_subscriptions")}
						</p>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Scope</TableHead>
									<TableHead>Status</TableHead>
									<TableHead>Plan</TableHead>
									<TableHead>Qty</TableHead>
									<TableHead>Project</TableHead>
									<TableHead>Term end</TableHead>
									<TableHead />
								</TableRow>
							</TableHeader>
							<TableBody>
								{data.subscriptions.map((sub) => (
									<TableRow key={sub.id}>
										<TableCell>
											<Badge variant="outline">{sub.scope}</Badge>
										</TableCell>
										<TableCell>{sub.status}</TableCell>
										<TableCell>{sub.planName ?? sub.planItemPriceId}</TableCell>
										<TableCell>{sub.planQuantity ?? "—"}</TableCell>
										<TableCell>{sub.projectName ?? "—"}</TableCell>
										<TableCell>{formatDate(sub.currentTermEnd)}</TableCell>
										<TableCell>
											{sub.pendingChange && (
												<Badge variant="secondary">
													{t("admin:sales.detail.pending_change")} (
													{formatDate(sub.pendingChange.effectiveAt)})
												</Badge>
											)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
